import {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
    getGridDateOperators,
    type GridColDef,
    type GridFilterInputValueProps,
    type GridFilterItem,
    type GridFilterModel,
    type GridFilterOperator,
    type GridPaginationModel,
    type GridRowId,
    type GridSortModel,
} from "@mui/x-data-grid";
import {useTranslation} from "react-i18next";
import {useAdminCallRecords, useAdminLegalEntities, useAdminUsers} from "../../api/admin/adminQueries";
import {renderer} from "../../util/renderer";
import AdminDataGrid from "./AdminDataGrid";
import SectionShell from "./AdminShared";
import {type AuditRow} from "./AdminTypes";

const collapsedTextCellHeight = 180;
const supportedCreatedAtOperators = ["is", "after", "onOrAfter", "before", "onOrBefore"] as const;

function shiftIsoDate(dateValue: string, days: number) {
    const [year, month, day] = dateValue.split("-").map(Number);
    const shiftedDate = new Date(Date.UTC(year, month - 1, day + days));

    return shiftedDate.toISOString().slice(0, 10);
}

function getBetweenValue(value: GridFilterItem["value"]) {
    if (!Array.isArray(value)) {
        return {from: "", to: ""};
    }

    const [from, to] = value;
    return {
        from: typeof from === "string" ? from : "",
        to: typeof to === "string" ? to : "",
    };
}

function mapCreatedAtFilterToRequest(filterModel: GridFilterModel) {
    const createdAtFilter = filterModel.items.find((item) => item.field === "createdAt");

    if (!createdAtFilter) {
        return {createdAtFrom: undefined, createdAtTo: undefined};
    }

    if (createdAtFilter.operator === "between") {
        const {from, to} = getBetweenValue(createdAtFilter.value);
        return {
            createdAtFrom: from || undefined,
            createdAtTo: to || undefined,
        };
    }

    if (typeof createdAtFilter.value !== "string" || !createdAtFilter.value) {
        return {createdAtFrom: undefined, createdAtTo: undefined};
    }

    switch (createdAtFilter.operator) {
        case "is":
            return {createdAtFrom: createdAtFilter.value, createdAtTo: createdAtFilter.value};
        case "after":
            return {createdAtFrom: shiftIsoDate(createdAtFilter.value, 1), createdAtTo: undefined};
        case "onOrAfter":
            return {createdAtFrom: createdAtFilter.value, createdAtTo: undefined};
        case "before":
            return {createdAtFrom: undefined, createdAtTo: shiftIsoDate(createdAtFilter.value, -1)};
        case "onOrBefore":
            return {createdAtFrom: undefined, createdAtTo: createdAtFilter.value};
        default:
            return {createdAtFrom: undefined, createdAtTo: undefined};
    }
}

const CreatedAtBetweenFilterInput = ({item, applyValue, focusElementRef, disabled}: GridFilterInputValueProps) => {
    const {t: translate} = useTranslation();
    const {from, to} = getBetweenValue(item.value);

    return (
        <Stack direction="row" spacing={1} sx={{width: "100%"}}>
            <TextField
                label={translate("admin.shared.from")}
                type="date"
                size="small"
                value={from}
                disabled={disabled}
                inputRef={focusElementRef}
                onChange={(event) => {
                    applyValue({...item, value: [event.target.value, to]});
                }}
                InputLabelProps={{shrink: true}}
                sx={{minWidth: 156}}
            />
            <TextField
                label={translate("admin.shared.to")}
                type="date"
                size="small"
                value={to}
                disabled={disabled}
                onChange={(event) => {
                    applyValue({...item, value: [from, event.target.value]});
                }}
                InputLabelProps={{shrink: true}}
                sx={{minWidth: 156}}
            />
        </Stack>
    );
};

const AuditingTab = () => {
    const {t: translate} = useTranslation();
    const [selectedAuditLegalEntity, setSelectedAuditLegalEntity] = useState("");
    const [selectedAuditUser, setSelectedAuditUser] = useState("all");
    const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({page: 0, pageSize: 25});
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: "createdAt", sort: "desc"}]);
    const [snapshotCreatedBefore, setSnapshotCreatedBefore] = useState<string>();
    const [expandedRowId, setExpandedRowId] = useState<GridRowId | null>(null);
    const legalEntitiesQuery = useAdminLegalEntities();
    const auditUsersQuery = useAdminUsers(selectedAuditLegalEntity || undefined);
    const {createdAtFrom, createdAtTo} = useMemo(() => mapCreatedAtFilterToRequest(filterModel), [filterModel]);
    const auditQueryParams = useMemo(
        () =>
            selectedAuditLegalEntity
                ? {
                      page: paginationModel.page,
                      pageSize: paginationModel.pageSize,
                      legalEntityId: selectedAuditLegalEntity,
                      userId: selectedAuditUser === "all" ? undefined : selectedAuditUser,
                      createdAtFrom,
                      createdAtTo,
                      sortColumns: sortModel
                          .filter((sortItem) => sortItem.sort === "asc" || sortItem.sort === "desc")
                          .map((sortItem) => ({
                              field: sortItem.field,
                              direction: sortItem.sort as "asc" | "desc",
                          })),
                      snapshotCreatedBefore,
                  }
                : undefined,
        [
            createdAtFrom,
            createdAtTo,
            paginationModel.page,
            paginationModel.pageSize,
            selectedAuditLegalEntity,
            selectedAuditUser,
            snapshotCreatedBefore,
            sortModel,
        ],
    );
    const callRecordsQuery = useAdminCallRecords(auditQueryParams);
    const legalEntities = legalEntitiesQuery.data ?? [];
    const auditUsers = auditUsersQuery.data ?? [];
    const auditRows = useMemo(() => callRecordsQuery.data?.items ?? [], [callRecordsQuery.data?.items]);
    const totalAuditRows = callRecordsQuery.data?.totalElements ?? 0;
    const createdAtFilterOperators = useMemo<GridFilterOperator<AuditRow>[]>(
        () => [
            ...getGridDateOperators()
                .filter((operator) =>
                    supportedCreatedAtOperators.includes(
                        operator.value as (typeof supportedCreatedAtOperators)[number],
                    ),
                )
                .map((operator) => ({
                    ...operator,
                    label: translate(`admin.auditing.filters.${operator.value}`),
                })),
            {
                label: translate("admin.auditing.filters.between"),
                value: "between",
                getApplyFilterFn: (filterItem) => {
                    const {from, to} = getBetweenValue(filterItem.value);

                    if (!from || !to) {
                        return null;
                    }

                    const fromTime = new Date(`${from}T00:00:00`).getTime();
                    const toTime = new Date(`${to}T23:59:59.999`).getTime();

                    return (value) => {
                        if (!(value instanceof Date)) {
                            return false;
                        }

                        const valueTime = value.getTime();
                        return valueTime >= fromTime && valueTime <= toTime;
                    };
                },
                InputComponent: CreatedAtBetweenFilterInput,
                getValueAsString: (value) => {
                    const {from, to} = getBetweenValue(value);
                    return from && to ? `${from} - ${to}` : "";
                },
            },
        ],
        [translate],
    );

    useEffect(() => {
        if (!snapshotCreatedBefore && callRecordsQuery.data?.snapshotCreatedBefore) {
            setSnapshotCreatedBefore(callRecordsQuery.data.snapshotCreatedBefore);
        }
    }, [callRecordsQuery.data?.snapshotCreatedBefore, snapshotCreatedBefore]);

    useEffect(() => {
        setExpandedRowId(null);
    }, [auditRows]);

    const renderExpandableTextCell = useCallback(
        (value: string, rowId: GridRowId) => {
            const isExpanded = expandedRowId === rowId;

            return (
                <Box sx={{py: 1.5, width: "100%"}}>
                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflow: "hidden",
                            maxHeight: isExpanded ? "none" : collapsedTextCellHeight,
                        }}
                    >
                        {value || "—"}
                    </Typography>
                </Box>
            );
        },
        [expandedRowId],
    );

    const auditColumns = useMemo<GridColDef<AuditRow>[]>(
        () => [
            {
                field: "userName",
                headerName: translate("admin.shared.user"),
                minWidth: 180,
                flex: 0.9,
                filterable: false,
            },
            {
                field: "inputTokens",
                headerName: translate("admin.auditing.columns.inputTokensUsed"),
                minWidth: 170,
                flex: 0.9,
                valueGetter: (_value, row) => renderer.formatNumber(row.inputTokens),
                filterable: false,
            },
            {
                field: "outputTokens",
                headerName: translate("admin.auditing.columns.outputTokensUsed"),
                minWidth: 180,
                flex: 0.9,
                valueGetter: (_value, row) => renderer.formatNumber(row.outputTokens),
                filterable: false,
            },
            {field: "model", headerName: translate("admin.shared.model"), minWidth: 160, flex: 0.8, filterable: false},
            {
                field: "response",
                headerName: translate("admin.auditing.columns.response"),
                minWidth: 260,
                flex: 1.4,
                sortable: false,
                filterable: false,
                renderCell: (params) => renderExpandableTextCell(params.row.response, params.id),
            },
            {
                field: "latestMessage",
                headerName: translate("admin.auditing.columns.latestMessage"),
                minWidth: 260,
                flex: 1.4,
                sortable: false,
                filterable: false,
                renderCell: (params) => renderExpandableTextCell(params.row.latestMessage, params.id),
            },
            {
                field: "language",
                headerName: translate("admin.shared.language"),
                minWidth: 120,
                flex: 0.6,
                filterable: false,
                valueGetter: (_value, row) => row.language.toUpperCase(),
            },
            {
                field: "systemPrompt",
                headerName: translate("admin.auditing.columns.systemPrompt"),
                minWidth: 260,
                flex: 1.3,
                sortable: false,
                filterable: false,
                renderCell: (params) => renderExpandableTextCell(params.row.systemPrompt, params.id),
            },
            {
                field: "createdAt",
                headerName: translate("admin.shared.createdAt"),
                type: "date",
                minWidth: 190,
                flex: 0.9,
                filterOperators: createdAtFilterOperators,
                valueGetter: (_value, row) => new Date(row.createdAt),
                renderCell: (params) => renderer.formatDate(params.row.createdAt),
            },
        ],
        [createdAtFilterOperators, renderExpandableTextCell, translate],
    );

    return (
        <SectionShell
            eyebrow={translate("admin.auditing.eyebrow")}
            title={translate("admin.auditing.title")}
            action={
                <Tooltip title={translate("admin.auditing.refreshSnapshot")}>
                    <span>
                        <IconButton
                            onClick={() => {
                                setSnapshotCreatedBefore(undefined);
                                setPaginationModel((currentValue) => ({...currentValue, page: 0}));
                            }}
                            disabled={!selectedAuditLegalEntity || callRecordsQuery.isFetching}
                            size="small"
                        >
                            <RefreshRoundedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            }
        >
            <Stack spacing={3}>
                {legalEntitiesQuery.error ? <Alert severity="error">{legalEntitiesQuery.error.message}</Alert> : null}
                {auditUsersQuery.error ? <Alert severity="error">{auditUsersQuery.error.message}</Alert> : null}
                {callRecordsQuery.error ? <Alert severity="error">{callRecordsQuery.error.message}</Alert> : null}

                <Stack direction={{xs: "column", lg: "row"}} spacing={2} flexWrap="wrap">
                    <FormControl fullWidth sx={{maxWidth: 340}} disabled={legalEntitiesQuery.isLoading}>
                        <InputLabel id="audit-legal-entity-label">{translate("admin.shared.legalEntity")}</InputLabel>
                        <Select
                            labelId="audit-legal-entity-label"
                            label={translate("admin.shared.legalEntity")}
                            value={selectedAuditLegalEntity}
                            onChange={(event) => {
                                setSelectedAuditLegalEntity(event.target.value);
                                setSelectedAuditUser("all");
                                setPaginationModel((currentValue) => ({...currentValue, page: 0}));
                            }}
                        >
                            {legalEntities.map((legalEntity) => (
                                <MenuItem key={legalEntity.id} value={legalEntity.id}>
                                    {legalEntity.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        fullWidth
                        sx={{maxWidth: 320}}
                        disabled={!selectedAuditLegalEntity || auditUsersQuery.isLoading}
                    >
                        <InputLabel id="audit-user-label">{translate("admin.shared.user")}</InputLabel>
                        <Select
                            labelId="audit-user-label"
                            label={translate("admin.shared.user")}
                            value={selectedAuditUser}
                            onChange={(event) => {
                                setSelectedAuditUser(event.target.value);
                                setPaginationModel((currentValue) => ({...currentValue, page: 0}));
                            }}
                        >
                            <MenuItem value="all">{translate("admin.shared.allUsers")}</MenuItem>
                            {auditUsers.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Box sx={{position: "relative"}}>
                    {!selectedAuditLegalEntity ? (
                        <Paper
                            elevation={0}
                            sx={(theme) => ({
                                position: "absolute",
                                inset: 0,
                                zIndex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 3,
                                bgcolor: alpha(
                                    theme.palette.background.default,
                                    theme.palette.mode === "light" ? 0.88 : 0.94,
                                ),
                                border: "1px dashed",
                                borderColor: alpha(theme.palette.primary.main, 0.28),
                            })}
                        >
                            <Stack spacing={1} alignItems="center">
                                <ReceiptLongRoundedIcon sx={{fontSize: 34, color: "primary.main"}} />
                                <Typography sx={{fontWeight: 700, color: "text.primary"}}>
                                    {translate("admin.auditing.emptyState")}
                                </Typography>
                            </Stack>
                        </Paper>
                    ) : null}

                    <Box sx={{opacity: selectedAuditLegalEntity ? 1 : 0.35}}>
                        <AdminDataGrid
                            rows={auditRows}
                            columns={auditColumns}
                            autoHeight
                            loading={callRecordsQuery.isLoading || callRecordsQuery.isFetching}
                            pagination
                            paginationMode="server"
                            rowCount={totalAuditRows}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            rowHeight={64}
                            getRowHeight={() => "auto"}
                            onRowClick={(params) => {
                                setExpandedRowId((currentValue) => (currentValue === params.id ? null : params.id));
                            }}
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={(nextSortModel) => {
                                setSortModel(nextSortModel);
                                setPaginationModel((currentValue) => ({...currentValue, page: 0}));
                            }}
                            filterMode="server"
                            filterModel={filterModel}
                            onFilterModelChange={(nextFilterModel) => {
                                setFilterModel(nextFilterModel);
                                setPaginationModel((currentValue) => ({...currentValue, page: 0}));
                            }}
                            pageSizeOptions={[25, 50, 100]}
                            sx={{
                                ".MuiDataGrid-row": {
                                    cursor: "pointer",
                                },
                                ".MuiDataGrid-cell": {
                                    alignItems: "flex-start",
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Stack>
        </SectionShell>
    );
};

export default AuditingTab;
